import { useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import userService, { UserProfile } from '../services/endpoints/userService';

interface UseCurrentUserResult {
  user: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook that provisions/fetches the current user's profile from the backend.
 * Called once after authentication. On first login, the backend auto-creates
 * the user record. Subsequent logins sync profile changes and update LastLoginAt.
 *
 * The returned `user.firstName` is used for the personalized dashboard greeting.
 */
export function useCurrentUser(): UseCurrentUserResult {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) {
      setUser(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const profile = await userService.getOrCreateCurrentUser();
      setUser(profile);
    } catch (err: any) {
      console.error('Failed to load user profile:', err);
      setError('Unable to load your profile. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, accounts]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return { user, isLoading, error, refetch: fetchUser };
}
